import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { MouseEvent, ReactNode } from 'react';
import { IconType } from 'react-icons/lib';

interface ISuccessButtonProps {
    id?: string;
    loading?: boolean;
    children: ReactNode;
    onClick?: (event: MouseEvent) => void;
}

export const SuccessButton = ({ id, loading, children, onClick }: ISuccessButtonProps) => {
    return (
        <Box style={{ height: '100%' }}>
            <Button
                {...(id ? { id } : {})}
                color="primary"
                type="submit"
                sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    width: '100%',
                    '&:hover': {
                        backgroundColor: 'primary.main',
                    },
                    height: '100%',
                }}
                onClick={onClick}
            >
                {!loading && <>{children}</>}
                {loading && <CircularProgress size={24} color="inherit" />}
            </Button>
        </Box>
    );
};

interface IPrimaryButtonProps {
    id?: string;
    loading?: boolean;
    children: string;
    onClick?: (event: MouseEvent) => void;
    variant?: 'solid' | 'gradient';
    fontSize?: string;
}

export const PrimaryButton = ({ id, loading, children, onClick, variant = 'solid', fontSize }: IPrimaryButtonProps) => {
    return (
        <Box style={{ height: '100%' }}>
            <Button
                {...(id ? { id } : {})}
                color="primary"
                type="button"
                style={{ ...(fontSize ? { fontSize } : {}) }}
                onClick={onClick}
                sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    width: '100%',
                    '&:hover': {
                        backgroundColor: 'primary.main',
                    },
                    height: '100%',
                    ...(variant === 'gradient'
                        ? {
                              borderRadius: '100px',
                              background: 'linear-gradient(115.87deg, #D8042A 0%, #FE8C67 100%)',
                              padding: '10px 20px',
                              fontSize: '1.5rem',
                              boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.15)',
                          }
                        : {}),
                }}
            >
                {!loading && <>{children}</>}
                {loading && <CircularProgress size={24} color="inherit" />}
            </Button>
        </Box>
    );
};

export const SecondaryButton = ({ id, loading, children, onClick }: IPrimaryButtonProps) => {
    return (
        <Box style={{ height: '100%', backgroundColor: 'white' }}>
            <Button
                {...(id ? { id } : {})}
                color="inherit"
                type="button"
                onClick={onClick}
                style={{ outline: 'none' }}
                sx={{
                    backgroundColor: 'white',
                    color: 'primary.main',
                    width: '100%',
                    '&:hover': {
                        backgroundColor: 'white',
                    },
                    height: '100%',
                    boxShadow: '0 2px 4px 1px rgba(85, 88, 219, 0.15)',
                    borderRadius: 0,
                    textTransform: 'none',
                    outline: 'none',
                }}
            >
                {!loading && <>{children}</>}
                {loading && <CircularProgress size={24} color="inherit" />}
            </Button>
        </Box>
    );
};

interface ILoginContinueButtonProps {
    onClick?: (event: MouseEvent) => void;
    children: ReactNode;
    Icon: IconType;
    iconWidth?: string;
    disabled?: boolean;
}

export const LoginContinueButton = ({ onClick, children, Icon, iconWidth, disabled }: ILoginContinueButtonProps) => {
    return (
        <Button
            sx={{
                backgroundColor: 'white',
                border: '1px solid #757575',
                width: '100%',
                borderRadius: '5px',
                color: '#757575',
                maxWidth: '350px',
                textAlign: 'left',
                padding: '8px 16px',
                '&:hover': {
                    backgroundColor: 'white',
                },
            }}
            onClick={onClick}
            disabled={disabled}
        >
            <Icon
                style={{
                    position: 'absolute',
                    left: '16px',
                    fontSize: '20px',
                    ...(iconWidth ? { width: iconWidth } : {}),
                }}
            />
            <Typography
                sx={{
                    color: '#757575',
                    fontSize: '14px',
                    textTransform: 'none',
                    verticalAlign: 'middle',
                    fontWeight: 500,
                }}
            >
                {children}
            </Typography>
        </Button>
    );
};
